import React, { useState } from "react";
import { Link } from "react-router-dom";

// --- IMPORT YOUR REAL STYLED COMPONENTS HERE ---
import Overview from "./Overview";
import AdminProducts from "./AdminProducts";
import AdminCategories from "./AdminCategories";
import AdminUsers from "./AdminUsers";
import AdminListedProducts from "./AdminListedProducts";

const AdminDashboard = () => {
  const [tab, setTab] = useState("overview");
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // This function decides which page to show based on the clicked tab
  const renderPage = () => {
    if (tab === "overview") return <Overview setTab={setTab} />; 
    if (tab === "products") return <AdminProducts />;
    if (tab === "categories") return <AdminCategories />;
    if (tab === "users") return <AdminUsers />;
    if (tab === "listed") return <AdminListedProducts />;
  };

  // --- TAB BUTTON STYLING (MATCHING IMAGE) ---
  const getTabButtonStyle = (isActive) => {
    const isMobile = windowWidth <= 768;
    const isTablet = windowWidth <= 1024;
    
    return {
      flex: isMobile ? "1 1 20%" : "1 1 0%", 
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: isMobile ? "4px" : "8px",
      padding: isMobile ? "10px 6px" : "clamp(12px, 2vw, 18px) clamp(8px, 1.5vw, 16px)",
      fontSize: isMobile ? "11px" : "clamp(11px, 1.2vw, 14px)",
      fontWeight: isActive ? "700" : "500",
      color: isActive ? "#112347" : "#6b7280",
      backgroundColor: isActive ? "#ffffff" : "transparent",
      border: "none",
      borderBottom: isActive ? "4px solid #112347" : "4px solid transparent",
      borderRight: isMobile ? "none" : "1px solid #e5e7eb",
      transition: "all 0.2s ease",
      cursor: "pointer",
      outline: "none",
      whiteSpace: "nowrap",
      minWidth: isMobile ? "50px" : "0",
      overflow: "hidden"
    };
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fa" }}>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @media (max-width: 768px) {
          .tab-button {
            position: relative;
          }

          .tab-button:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: #112347;
            color: white;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            white-space: nowrap;
            z-index: 1000;
            margin-bottom: 8px;
            pointer-events: none;
          }

          .tab-button:hover::before {
            content: '';
            position: absolute;
            bottom: calc(100% - 2px);
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-top-color: #112347;
            z-index: 1000;
            pointer-events: none;
          }
        }
      `}</style>
      
      {/* 1. DARK BLUE HERO SECTION */}
      <div
        style={{
          background: "#112347",
          color: "#fff",
          padding: "clamp(20px, 4vw, 40px) clamp(15px, 4vw, 40px)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <div style={{ flex: "1 1 auto", minWidth: "200px" }}>
          <h2 style={{ margin: 0, fontWeight: "700", fontSize: "clamp(24px, 4vw, 32px)" }}>Admin Dashboard</h2>
          <p style={{ margin: "5px 0 0 0", color: "#94a3b8", fontSize: "clamp(13px, 1.5vw, 16px)" }}>
            Manage products, categories, and users
          </p>
        </div>
        <Link
          to="/"
          className="d-flex align-items-center gap-1 gap-md-2 text-decoration-none flex-shrink-0"
          style={{
            minWidth: "auto",
            border: "1px solid rgba(255,255,255,0.3)",
            background: "rgba(255,255,255,0.05)",
            color: "white",
            padding: "8px 20px",
            fontSize: "clamp(12px, 1.5vw, 14px)",
            borderRadius: "6px",
            whiteSpace: "nowrap",
            flex: "0 0 auto"
          }}
        >
          View Site
        </Link>
      </div>

      {/* 2. TAB MENU BAR */}
      <div style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb", width: "100%" }}>
        
        {/* Tab Menu */}
        <div
          className="tab-menu"
          style={{
            display: "flex",
            background: "#f6f5f3",
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollBehavior: "smooth"
          }}
        >
          <button 
            className="tab-button" 
            style={getTabButtonStyle(tab === "overview")} 
            onClick={() => setTab("overview")}
            title={windowWidth <= 768 ? "Overview" : ""}
            data-tooltip="Overview"
          >
            <span style={{fontSize: 'clamp(14px, 2vw, 18px)'}}>📊</span> 
            {windowWidth > 1024 && <span style={{ display: "inline-block" }}>Overview</span>}
          </button>
          
          <button 
            className="tab-button" 
            style={getTabButtonStyle(tab === "products")} 
            onClick={() => setTab("products")}
            title={windowWidth <= 768 ? "Products" : ""}
            data-tooltip="Products"
          >
            <span style={{fontSize: 'clamp(14px, 2vw, 18px)'}}>📦</span> 
            {windowWidth > 1024 && <span style={{ display: "inline-block" }}>Products</span>}
          </button>
          
          <button 
            className="tab-button" 
            style={getTabButtonStyle(tab === "categories")} 
            onClick={() => setTab("categories")}
            title={windowWidth <= 768 ? "Categories" : ""}
            data-tooltip="Categories"
          >
            <span style={{fontSize: 'clamp(14px, 2vw, 18px)'}}>🔳</span> 
            {windowWidth > 1024 && <span style={{ display: "inline-block" }}>Categories</span>}
          </button>
          
          <button 
            className="tab-button" 
            style={getTabButtonStyle(tab === "users")} 
            onClick={() => setTab("users")}
            title={windowWidth <= 768 ? "Users" : ""}
            data-tooltip="Users"
          >
            <span style={{fontSize: 'clamp(14px, 2vw, 18px)'}}>👥</span> 
            {windowWidth > 1024 && <span style={{ display: "inline-block" }}>Users</span>}
          </button>

          <button 
            className="tab-button" 
            style={getTabButtonStyle(tab === "listed")} 
            onClick={() => setTab("listed")}
            title={windowWidth <= 768 ? "Listed" : ""}
            data-tooltip="Listed"
          >
            <span style={{fontSize: 'clamp(14px, 2vw, 18px)'}}>📋</span> 
            {windowWidth > 1024 && <span style={{ display: "inline-block" }}>Listed</span>}
          </button>
        </div>
      </div>

      {/* 3. DYNAMIC CONTENT AREA (Renders the full styled pages) */}
      <div className="container-fluid" style={{ padding: "clamp(15px, 3vw, 40px) clamp(10px, 3vw, 40px)" }}>
        {renderPage()}
      </div>

    </div>
  );
};

export default AdminDashboard;